"use client";

import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { ShoppingCart, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@/components/ui/responsive-modal";

interface AddToCartConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddToCartConfirmation({
  open,
  onOpenChange,
}: AddToCartConfirmationProps) {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("product.booking.cartConfirmation");

  const handleGoToCart = () => {
    onOpenChange(false);
    router.push(`/${locale}/cart`);
  };

  const handleContinueShopping = () => {
    onOpenChange(false);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("title")}
      description={t("description")}
      showCloseButton={false}
      srOnlyHeader
      footer={
        <div className="flex w-full gap-3">
          <Button
            onClick={handleContinueShopping}
            variant="outline"
            className="flex-1 h-12"
          >
            {t("continueShopping")}
          </Button>
          <Button
            onClick={handleGoToCart}
            className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {t("goToCart")}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
          <Check className="w-7 h-7 text-green-600" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold">{t("title")}</h3>
          <p className="text-sm text-gray-600">{t("description")}</p>
        </div>
      </div>
    </ResponsiveModal>
  );
}
